import cn from 'classnames';
import hHidden from '@creditflowresearch/uilib/styles/helpers/hidden.module.scss';
import styles from './spinner.module.scss';

export type Size = 'XS' | 'S' | 'M' | 'L' | 'XL';
export type Color = 'default' | 'on-color' | 'success' | 'error' | 'primary' | 'warning' | 'currentColor';

export interface IProps {
  /**
   * Size of the Spinner
   */
  size?: Size,
  /**
   * Color theme of the Spinner
   */
  color?: Color,
  /**
   * Determines if the spinner is displayed inline with other content.
   */
  inline?: boolean,
  /**
   * Centers the Spinner within its container.
   */
  centered?: boolean,
  /**
   * Indicates whether an overlay should be displayed behind the Spinner.
   */
  overlay?: boolean,
  /**
   * Controls the visibility of the Spinner.
   */
  isVisible?: boolean,
  /**
   * Fixes the spinner's position relative to the viewport.
   */
  isFixed?: boolean,
  /**
   * Fixes the spinner's position within its container.
   */
  isSpinnerFixed?: boolean,
  /**
   * Positions the spinner absolutely within its container.
   */
  isAbsolute?: boolean,
  /**
   * CSS class applied to the overlay container, if overlay is enabled.
   */
  overlayClassName?: string,
  /**
   * Additional CSS class for custom styling of the Spinner.
   */
  className?: string,
}

const sizeClassMap: Record<Size, string | undefined> = {
  XS: styles.sizeXs,
  S: styles.sizeS,
  M: styles.sizeM,
  L: undefined,
  XL: styles.sizeXl,
};

const colorClassMap: Record<Color, string | undefined> = {
  default: undefined,
  'on-color': styles.onColor,
  success: styles.success,
  error: styles.error,
  primary: styles.primary,
  warning: styles.warning,
  currentColor: styles.currentColor,
};

const Spinner = ({
  size = 'L',
  color = 'default',
  isVisible = true,
  isFixed = false,
  isSpinnerFixed = false,
  isAbsolute = false,
  inline = false,
  centered = false,
  ...props
}: IProps) => {
  const spinnerCls = cn(styles.spinner, sizeClassMap[size], colorClassMap[color], props.className, {
    [styles.isInline]: inline,
    [styles.centered]: centered,
    [hHidden.hidden]: !isVisible,
    [styles.spinnerFixed]: isSpinnerFixed,
  });
  const spinnerOverlayCls = cn(styles.spinnerOverlay, props.overlayClassName, {
    [hHidden.hidden]: !isVisible,
    [styles.spinnerOverlayFixed]: isFixed,
    [styles.spinnerOverlayAbsolute]: isAbsolute,
  });
  const Component = (
    <div
      className={ spinnerCls }
      data-test="loader"
      role="status"
      aria-label="Loading"
    />
  );
  if (props.overlay) {
    return (
      <div className={ spinnerOverlayCls }>
        { Component }
      </div>
    );
  }
  return Component;
};

export default Spinner;
